import React, { useEffect, useRef, useState } from 'react';

import RegistrationInvestigationsSection from '../visit/RegistrationInvestigationsSection';
import { adviseLabOrder } from '../../services/api/labApi';
import { openCombinedReceiptPdf } from '../../services/api/patientPlanApi';
import { newIdempotencyKey } from '../../services/api/admissionPaymentsApi';
import { fetchStaffList } from '../../services/api/staffApi';
import { selectLabTechnicians, technicianId, technicianName } from '../../utils/labTechnicianRoster';
import { usePermission, usePermissions } from '../../context/PermissionContext';
import DoctorPicker from '../procedures/DoctorPicker';
import { STAFF_ROLES, STAFF_STATUS } from '../../constants/staff';
import { CREDIT_SETTLE_PAYMENT_MODES, PAYMENT_MODE_DEFAULT, PAYMENT_PERMISSIONS } from '../../constants/admission';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const modeChipOn = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-teal-600 border-teal-600 text-white';
const modeChipOff = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700';

// How long the success banner stays up before the dialog auto-closes — long
// enough to read, short enough not to feel stuck (mirrors AdmissionDeskPage's
// own `flash()` notice pattern). NOT used when a collect_now leg settled —
// a receipt is money, so the dialog stays open until the desk explicitly
// closes it (see `receipts.length` below).
const AUTO_CLOSE_MS = 900;

// The exact bk-100 advise body for one picked line: patient + catalog-or-
// external + admission context + billing. NO visit_id (the backend mints the
// synthetic LAB visit). An external ("done outside", no catalog entry) line
// carries is_external + test_name; an in-house line carries
// catalog_procedure_id.
//
// LAB-ASSIGN/T1 — every IN-HOUSE line in the submit carries the SAME picked
// technician (`assigned_to_member_id` + `assigned_to_role`, both pre-existing
// fields on `POST /lab/orders` — no contract change).
//
// bk-1090 (owner ruling: EXEMPT) — an EXTERNAL "done outside" line is work
// somebody else already did, so it is NEVER assigned: the technician fields
// are omitted from its body even when the desk picked a technician for the
// in-house lines on the same submit. The server ignores a supplied assignee
// on an external line too, so this is belt-and-braces — but the request body
// should say what we mean. `technicianMemberId` absent (all-already-advised
// zero-send path) → the fields are simply omitted, byte-identical to before.
//
// `l.billing_mode` (set by buildInvestigationLines from the desk's per-row
// toggle) drives the body: 'collect_now' → collect_now + payment_mode +
// reference_number (non-cash) + a FRESH idempotency_key (one per line, per
// submit — collect_now mints its own order/settle each call, so a shared key
// across lines would misroute the BE's idem short-circuit). Anything else
// (including absent) → 'credit', unchanged from the pre-existing behaviour.
// The client never sends an amount — the server prices from the catalog.
//
// BUSY-VOUCHER R1.1 (OR-60) — EVERY line (in-house AND external) carries
// `advising_doctor_member_id`: the picked doctor's member id, or an explicit
// `null` = SELF (no origin doctor stamped, even for a doctor caller). The key
// is always present — absent would fall back to "stamp the caller if a doctor".
function _adviseBody(admission, l, { paymentMode, referenceNumber, technicianMemberId, advisingDoctorMemberId } = {}) {
  const collectNow = l.billing_mode === 'collect_now';
  const body = {
    patient_id: admission.patient_id,
    patient_name: admission.patient_name,
    quantity: Number(l.quantity) || 1,
    admission_id: l.admission_id || admission.admission_id,
    ip_number: l.ip_number || admission.ip_number || null,
    billing_mode: collectNow ? 'collect_now' : 'credit',
    advising_doctor_member_id: advisingDoctorMemberId || null,
  };
  if (collectNow) {
    body.payment_mode = paymentMode;
    if (referenceNumber) body.reference_number = referenceNumber;
    body.idempotency_key = newIdempotencyKey('lab-collect');
  }
  if (l.is_external) {
    body.is_external = true;
    body.test_name = l.test_name || l.name;
  } else {
    body.catalog_procedure_id = l.procedure_id;
  }
  if (technicianMemberId && !l.is_external) {
    body.assigned_to_member_id = technicianMemberId;
    body.assigned_to_role = STAFF_ROLES.LAB_TECHNICIAN;
  }
  return body;
}

/**
 * OrderLabDialog (day-1 ORCHESTRATOR ADDENDUM-4/S8-THIN → round-2 B2 REAL
 * FIX → collect-now FIX) — the admission-side "Order Diagnostic" entry point. Hosts
 * the EXISTING `RegistrationInvestigationsSection` picker (same as the
 * register-visit form) with the admission's `admissionId`/`ipNumber` stamped
 * additively onto every picked line (`buildInvestigationLines`'s ipStamp).
 *
 * Round-2 B2 (bk-100 frozen contract) — "Place order" ADVISES:
 *   - Each `source:'new'` line → advise a fresh order (`POST /lab/orders`) with
 *     `patient_id` + catalog/external + `admission_id` + `ip_number`, NO
 *     `visit_id`, NO technician. The backend mints the synthetic LAB visit
 *     and the order enters the UNASSIGNED pull-queue (status `advised`); a
 *     technician claims it later (memory `lab_assignment_division`). There is
 *     NO assign call at order time.
 *   - A `source:'advised'` row is a test a doctor ALREADY advised — it is
 *     already sitting in that same pull-queue, so it needs no second order
 *     (counted as sent-to-lab, no API call — never re-advised/duplicated).
 * `billing_mode` rides straight through from the line the picker built
 * (`labInvestigationLines.js`) — this dialog no longer decides it:
 *   - `'credit'` (default, IP toggle ON) — unchanged bk-100 path.
 *   - `'collect_now'` (IP toggle switched to "Collect payment now") — the
 *     line also carries the desk's chosen `payment_mode`/`reference_number`/
 *     a fresh `idempotency_key`; the order comes back SETTLED (paid, with a
 *     receipt) instead of landing in the credit/Settle-card queue.
 * IPD lab is otherwise credit-only: a bare non-credit/non-collect_now advise
 * 422s `IPD_LAB_CREDIT_ONLY`, surfaced verbatim in the error banner.
 * All-success → explicit success banner ("ordered — sent for diagnostics"); if any
 * line settled (collect_now), the banner also shows the receipt number(s) +
 * a Print button and the dialog stays open (money receipt — never
 * auto-closed) until the desk closes it explicitly. No collect_now leg →
 * unchanged auto-close. ANY failure → machine-worded error banner IN-dialog,
 * picked lines RETAINED (the picker is never unmounted), dialog stays open.
 * No silent close, no empty catch (memory `trace_end_to_end_user_surface`).
 *
 * Single-flight via a ref guard (checked+set synchronously before the first
 * `await`) so a fast double-click can never fire two POSTs for the same pick
 * — the money-safety linchpin for collect_now (each line mints a fresh order
 * + idempotency_key; a double submit would double-charge without this guard).
 *
 * C2 — `onOrdered()` fires exactly once, after `setSuccess`, on the real
 * all-success path only (never on a failure, never on the zero-send no-op
 * below) — the desk's cue to bump its `diagnosticsRefreshToken` so the
 * Diagnostics tab / Settle card / Generate-bill button refresh without a
 * page reload. The success count is `toAdvise.length` — what was actually
 * POSTed — never `lines.length`, which also counts advised rows that place
 * no order at all; picking only advised rows sends nothing and shows no
 * success banner. "Print receipt" (after a collect-now settlement) is
 * gated on `PAYMENT_PERMISSIONS.GENERATE_RECEIPT_PDF`, the same slug every
 * sibling receipt-print action uses.
 */
export default function OrderLabDialog({ admission, onClose, onOrdered }) {
  const canPrintReceipt = usePermission(PAYMENT_PERMISSIONS.GENERATE_RECEIPT_PDF);
  const { role, memberId: viewerMemberId } = usePermissions();
  const isLabTechnicianCaller = (role || '').toUpperCase() === STAFF_ROLES.LAB_TECHNICIAN;
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  // Collect-now legs that settled on this submit — [{ payment_id, receipt_number }].
  const [settledReceipts, setSettledReceipts] = useState([]);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODE_DEFAULT);
  const [reference, setReference] = useState('');
  const inFlight = useRef(false);
  const closeTimer = useRef(null);

  // LAB-ASSIGN/T1+T2 — technician roster: 'loading' | 'loaded' | 'error'.
  // ONE fetch per dialog open + one extra per explicit Retry click (bumps
  // `retryToken`) — no polling (`runtime_safety_no_request_storms`).
  // `fetchStaffList()` is already a session-cached single-flight call
  // (`staffApi.js`), so a warm cache resolves this near-instantly.
  const [technicians, setTechnicians] = useState([]);
  const [rosterStatus, setRosterStatus] = useState('loading');
  const [selectedTechId, setSelectedTechId] = useState('');
  const [retryToken, setRetryToken] = useState(0);
  // R1.1 — advising-doctor roster (same fetch) + the pick. Seeded ONCE from
  // the admission's primary doctor; '' = SELF. Never re-seeded when the admission prop changes.
  const [doctors, setDoctors] = useState([]);
  const [advisingDoctorId, setAdvisingDoctorId] = useState(admission.primary_doctor_member_id || '');

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  useEffect(() => {
    let cancelled = false;
    setRosterStatus('loading');
    fetchStaffList()
      .then((r) => {
        if (cancelled) return;
        if (r && r.success) {
          setTechnicians(selectLabTechnicians(r));
          // ACTIVE doctors only (the server 422s anything else), any department.
          setDoctors((r.data || []).filter((m) => m.role === STAFF_ROLES.DOCTOR && (!m.status || m.status === STAFF_STATUS.ACTIVE)));
          setRosterStatus('loaded');
        } else {
          setRosterStatus('error');
        }
      })
      .catch(() => { if (!cancelled) setRosterStatus('error'); });
    return () => { cancelled = true; };
  }, [retryToken]);

  // Owner's rule, verbatim: EXACTLY one technician → pre-select (still
  // editable — AC4). 2+ → leave empty, an explicit pick is required. Guarded
  // on `!selectedTechId` so a manual pick (or a prior auto-pick) is never
  // clobbered by a later re-fetch/Retry.
  //
  // LAB-ASSIGN-2 / FE-4 — a LAB_TECHNICIAN caller (now granted ACCESS_IPD,
  // this dialog IS reachable for the role) is pre-selected as THEMSELVES
  // regardless of roster size, editable — a technician placing their own
  // order doesn't need to find their own name in a 2+ roster. Only applies
  // when their own id is actually present in `technicians`; otherwise the
  // placeholder shows and they pick explicitly like anyone else (no phantom
  // pre-select).
  useEffect(() => {
    if (rosterStatus !== 'loaded' || selectedTechId) return;
    if (isLabTechnicianCaller && viewerMemberId && technicians.some((t) => technicianId(t) === viewerMemberId)) {
      setSelectedTechId(viewerMemberId);
      return;
    }
    if (technicians.length === 1) {
      setSelectedTechId(technicianId(technicians[0]));
    }
  }, [rosterStatus, technicians, selectedTechId, isLabTechnicianCaller, viewerMemberId]);

  const retryRoster = () => setRetryToken((t) => t + 1);

  // R1.1 — the id actually POSTed. Until the roster settles the seeded id
  // stands (the picker will show it); once settled it must be in the active
  // list, else '' → SELF — so the payload always matches what the picker shows.
  const advisingListed = rosterStatus === 'loading' || doctors.some((d) => d.member_id === advisingDoctorId);
  const advisingDoctorMemberId = advisingDoctorId && advisingListed ? advisingDoctorId : null;

  // The roster "notice" — a load FAILURE ('error') and a successful load
  // that came back EMPTY ('empty') are distinct states with distinct
  // ratified copy (owner rulings): "Couldn't load the technician list." +
  // Retry vs "No lab technician on staff yet — add one under Manage Staff to
  // order tests." Exactly one of loading/error/empty/loaded renders at a
  // time; distinct data-testids keep the two conditions separately
  // assertable in tests.
  const rosterNotice = rosterStatus === 'error'
    ? 'error'
    : (rosterStatus === 'loaded' && technicians.length === 0 ? 'empty' : null);

  // Owner's rule: a technician is required only when this submit would
  // actually POST at least one non-external line (external/"done outside"
  // lines ride the technician along like any other line, but the server
  // exempts them — no assignment target is needed to place them). D4 — this
  // must hold regardless of roster state: gating it on `rosterStatus ===
  // 'loaded'` let a roster load FAILURE (or a submit fired mid-fetch) post a
  // non-external line with no technician at all, landing an unassigned
  // order the server will 422 once it enforces TECHNICIAN_REQUIRED. The
  // dialog must not offer that dead end in the first place.
  const needsTechnician = lines.some((l) => l.source !== 'advised' && !l.is_external);
  const technicianMissing = needsTechnician && !selectedTechId;

  const hasCollectNow = lines.some((l) => l.billing_mode === 'collect_now');
  const modeMeta = CREDIT_SETTLE_PAYMENT_MODES.find((m) => m.value === paymentMode) || CREDIT_SETTLE_PAYMENT_MODES[0];
  const needsReference = hasCollectNow && !!modeMeta.reference;
  const referenceValid = !needsReference || reference.trim().length > 0;

  const pickMode = (m) => {
    setPaymentMode(m);
    const meta = CREDIT_SETTLE_PAYMENT_MODES.find((x) => x.value === m);
    if (!meta || !meta.reference) setReference(''); // switching to cash → clear the reference
  };

  const handleSubmit = async () => {
    // single-flight: no double-post on a fast double-click (also the
    // collect_now money-safety guard — see class doc). `technicianMissing`
    // mirrors the button's own `disabled` — kept here too as defense in
    // depth against a race between roster state and a fast click.
    if (inFlight.current || lines.length === 0 || !referenceValid || technicianMissing) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    setSettledReceipts([]);

    // bk-100 frozen contract: a `new` line is a fresh order to place; an
    // `advised` line is already in the pull-queue (no second order). No
    // visit_id, no assign, no technician — the tech pulls it later.
    const toAdvise = lines.filter((l) => l.source !== 'advised');

    // Nothing to actually send — every picked line was already an
    // advised/unassigned row (the OPD-only path after C1; kept here so the
    // dialog can never claim an order it did not place). No POST, no
    // success banner, no onOrdered — a green tick with zero requests behind
    // it is the exact defect this fix removes.
    if (toAdvise.length === 0) {
      inFlight.current = false;
      setSubmitting(false);
      setError('Nothing to place — every picked test is already in the diagnostic queue.');
      return;
    }

    const results = await Promise.all(toAdvise.map((l) => adviseLabOrder(
      _adviseBody(admission, l, {
        paymentMode,
        referenceNumber: reference.trim(),
        technicianMemberId: selectedTechId,
        advisingDoctorMemberId,
      }),
    )));
    const failedCount = results.filter((r) => !r.success).length;

    if (failedCount > 0) {
      inFlight.current = false;
      setSubmitting(false);
      // Surface the server's own words when it explained itself (e.g. the
      // IPD_LAB_CREDIT_ONLY 422) — never a swallowed/blank failure.
      const firstFail = results.find((r) => !r.success);
      const firstErr = firstFail?.error;
      // A permission/403 failure is NOT transient — "try again" is misleading
      // (a retry never clears the permission). Show the server's own words
      // alone. Keep the reassuring "Nothing was lost — try again." wrapper for
      // genuinely transient failures.
      const isPermissionError = firstFail?.status === 403 || firstFail?.code === 'NOT_PERMITTED';
      if (isPermissionError && firstErr) {
        setError(firstErr);
        return;
      }
      const base =
        failedCount === results.length
          ? "Couldn't place the order. Nothing was lost — try again."
          : `${failedCount} of ${results.length} test(s) couldn't be ordered. Nothing was lost — try again.`;
      setError(firstErr ? `${base} (${firstErr})` : base);
      return;
    }

    // Collect_now legs come back settled (is_paid, receipt_number, payment_id
    // on the returned LabOrderResponse) — surface every receipt so the desk
    // can print it. A plain-credit line returns none of these.
    const receipts = results
      .map((r, i) => (toAdvise[i].billing_mode === 'collect_now' && r.data?.payment_id
        ? { payment_id: r.data.payment_id, receipt_number: r.data.receipt_number }
        : null))
      .filter(Boolean);

    setSubmitting(false);
    // Count what was ACTUALLY sent (toAdvise), never the rows picked (lines)
    // — an advised row rides along "sent for diagnostics" for free (already queued),
    // it is not a second POST, so it must not inflate this count either way.
    // Name the technician (AC9) only when one actually rode along on this
    // submit (selectedTechId) — an all-external submit needs none, and keeps
    // the plain "sent for diagnostics." wording.
    const bannerTechnician = selectedTechId
      ? technicians.find((t) => technicianId(t) === selectedTechId)
      : null;
    const bannerCount = `${toAdvise.length} test${toAdvise.length > 1 ? 's' : ''} ordered`;
    setSuccess(bannerTechnician
      ? `${bannerCount} — sent to ${technicianName(bannerTechnician)}'s diagnostic queue.`
      : `${bannerCount} — sent for diagnostics.`);
    if (typeof onOrdered === 'function') onOrdered();
    if (receipts.length > 0) {
      setSettledReceipts(receipts);
      // inFlight stays true, no auto-close — a settled receipt is money; the
      // desk closes explicitly once they've printed (or skipped) it.
    } else {
      // inFlight stays true — a closing dialog must not accept another submit.
      closeTimer.current = setTimeout(onClose, AUTO_CLOSE_MS);
    }
  };

  return (
    <div className={overlayCls} data-testid="order-lab-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Order Diagnostic</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2">
            <b className="text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</b> · {admission.ip_number}
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="order-lab-error">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 text-[12.5px] p-2.5 space-y-1.5" data-testid="order-lab-success">
              <div>{success}</div>
              {settledReceipts.length > 0 && (
                <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="order-lab-settled">
                  <span>
                    Collected — Receipt{settledReceipts.length > 1 ? 's' : ''} #{settledReceipts.map((r) => r.receipt_number).join(', #')}
                  </span>
                  {canPrintReceipt && (
                    <button
                      type="button"
                      onClick={() => openCombinedReceiptPdf(settledReceipts.map((r) => r.payment_id))}
                      className={btnSecondary}
                      data-testid="order-lab-print-receipt"
                    >
                      Print receipt
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {hasCollectNow && !success && (
            <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 p-3 space-y-2" data-testid="order-lab-collect-now-mode">
              {/* COPY: Business-ratified */}
              <div className="text-[12.5px] font-semibold text-teal-800 dark:text-teal-200">Payment mode</div>
              <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Payment mode">
                {CREDIT_SETTLE_PAYMENT_MODES.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => pickMode(m.value)}
                    className={paymentMode === m.value ? modeChipOn : modeChipOff}
                    data-testid={`order-lab-mode-${m.value}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {modeMeta.reference && (
                <div data-testid="order-lab-reference-field">
                  <label className={labelCls} htmlFor="ol-reference">{modeMeta.refLabel || 'Reference no.'}</label>
                  <input
                    id="ol-reference"
                    className={inputCls}
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    data-testid="order-lab-reference"
                  />
                </div>
              )}
            </div>
          )}

          {/* R1.1 (OR-60) — "Advised by doctor", directly ABOVE the investigations
              picker, in the BODY (not the footer): the DoctorPicker popup opens
              downward, so up here the whole list is visible without scrolling the
              panel (QA D1, 1366×768). Dropdown only: the DoctorPicker's
              type-another-name row is NOT wired (onTypeOther absent). ✕ Clear
              selection → SELF. */}
          <div role="group" aria-labelledby="ol-advising-label" data-testid="order-lab-advised-by">
            <div id="ol-advising-label" className={labelCls}>Advised by doctor</div>
            <DoctorPicker
              doctors={doctors}
              selectedId={advisingDoctorId || null}
              onChange={(id) => setAdvisingDoctorId(id || '')}
              isLoading={rosterStatus === 'loading'}
              placeholder="Select a doctor (optional)"
            />
            <div className="text-[11.5px] text-slate-600 dark:text-slate-300 mt-1" data-testid="order-lab-advised-by-helper">
              Leave empty for SELF
            </div>
          </div>
          <RegistrationInvestigationsSection
            patientId={admission.patient_id}
            departmentId={admission.department_id || ''}
            admissionId={admission.admission_id}
            ipNumber={admission.ip_number}
            onChange={setLines}
          />
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 space-y-2">
          {/* LAB-ASSIGN/T1+T2 — the technician field sits directly ABOVE
              Place order (mockup 01-order-lab-dialog.html), never below the
              unbounded test list. Exactly one of loading / error / empty /
              loaded renders. */}
          <div>
            <label className={labelCls} htmlFor="ol-technician">Lab technician</label>
            {rosterStatus === 'loading' && (
              <select id="ol-technician" className={inputCls} disabled data-testid="order-lab-technician-select">
                <option>Loading…</option>
              </select>
            )}
            {rosterNotice === 'error' && (
              <div
                className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5 flex items-center justify-between gap-2"
                data-testid="order-lab-technician-roster-error"
              >
                <span>Couldn&apos;t load the technician list.</span>
                <button
                  type="button"
                  onClick={retryRoster}
                  className="font-semibold underline text-teal-700 dark:text-teal-300"
                  data-testid="order-lab-technician-roster-retry"
                >
                  Retry
                </button>
              </div>
            )}
            {rosterNotice === 'empty' && (
              <div
                className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5"
                data-testid="order-lab-technician-roster-empty"
              >
                No lab technician on staff yet — add one under Manage Staff to order tests.
              </div>
            )}
            {rosterStatus === 'loaded' && !rosterNotice && (
              <>
                <select
                  id="ol-technician"
                  className={inputCls}
                  value={selectedTechId}
                  onChange={(e) => setSelectedTechId(e.target.value)}
                  data-testid="order-lab-technician-select"
                >
                  <option value="" disabled>Choose technician…</option>
                  {technicians.map((t) => (
                    <option key={technicianId(t)} value={technicianId(t)}>{technicianName(t)}</option>
                  ))}
                </select>
                {/* LAB-ASSIGN-2 / FE-4 — the technician's own self pre-select
                    always gets the frozen helper line, regardless of roster
                    size; every other pre-select (the pre-existing "exactly
                    one" rule) keeps its own unchanged copy. */}
                {isLabTechnicianCaller && selectedTechId && selectedTechId === viewerMemberId ? (
                  <div className="text-[11.5px] text-teal-700 dark:text-teal-300 mt-1">
                    Assigned to you. Change it if someone else will run the test.
                  </div>
                ) : (technicians.length === 1 && (
                  <div className="text-[11.5px] text-teal-700 dark:text-teal-300 mt-1">
                    Only lab technician on staff — selected for you.
                  </div>
                ))}
              </>
            )}
            {technicianMissing && (
              <div className="text-[11.5px] text-red-600 dark:text-red-400 mt-1" data-testid="order-lab-technician-reason">
                Choose the technician who will run this test.
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={onClose} data-testid="order-lab-cancel">{success ? 'Done' : 'Cancel'}</button>
            <button
              type="button"
              className={btnPrimary}
              onClick={handleSubmit}
              disabled={lines.length === 0 || submitting || !!success || !referenceValid || technicianMissing}
              data-testid="order-lab-submit"
            >
              {submitting ? 'Placing…' : 'Place order'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
